import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUserIdFromStorageOrToken } from '../utils/authHelpers';
import config from '../config';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [bookingCode, setBookingCode] = useState('');
  const [userId, setUserId] = useState('');
  const purpose = searchParams.get('purpose');

  const [bookingCodeConfirmed, setBookingCodeConfirmed] = useState(null); // null = waiting, true = ok, false = not found

  useEffect(() => {
    const booking = searchParams.get('bookingCode');
    const foundUserId = getUserIdFromStorageOrToken();
    setBookingCode(booking || '');
    setUserId(foundUserId || '');
  
    // Set appropriate message
    switch (purpose) {
      case 'user-upgrade':
        setMessage(t('payment.userUpgrade', 'Your account has been upgraded!'));
        setTimeout(() => navigate('/explorer'), 3000);
        break;
      case 'bike-full-day':
        setMessage(t('payment.bikeFullDay', 'Your full-day bike rental was successful!'));
        break;
      case 'bike-half-day':
        setMessage(t('payment.bikeHalfDay', 'Your half-day bike rental was successful!'));
        break;
      case 'business-subscription':
        setMessage(t('payment.businessSub', 'Thank you for subscribing as a business!'));
        break;
      case 'offer-purchase':
        setMessage(t('payment.offerPurchase', 'Thank you for your purchase!'));
        setTimeout(() => navigate('/explorer'), 3000);
        break;
      case 'bike-rental':
        setMessage(t('payment.bikeRental', 'Your bike rental was successful!'));
        break;
      default:
        setMessage(t('payment.success', 'Payment completed successfully!'));
    }
  
    // Trigger booking confirmation with delay
    if (purpose === 'bike-rental' && booking) {
      const delay = setTimeout(async () => {
        try {
          const res = await fetch(config.getApiUrl(`transactions/by-booking-code/${booking}`));        
          const text = await res.text();
          try {
            const data = JSON.parse(text);
            if (res.ok && data.success) {
              setBookingCodeConfirmed(true);
            } else {
              console.warn('Booking code not confirmed:', data);
              setBookingCodeConfirmed(false);
            }
          } catch (jsonErr) {
            console.error('❌ Could not parse JSON:', text);
            setBookingCodeConfirmed(false);
          }
        
        } catch (err) {
          console.error('Error confirming booking code:', err);
          setBookingCodeConfirmed(false);
        }        
      }, 1000);
  
      return () => clearTimeout(delay);
    }
  }, [searchParams, purpose, t, navigate]);
  
  const qrCodeUrl = bookingCode && userId
  ? `${window.location.origin}/booking/${bookingCode}/${userId}`
  : '';

  const handleClose = () => {
    navigate('/explorer');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-green-50 p-6">
      <div className="bg-white p-6 rounded-xl shadow-md text-center max-w-md space-y-6">
        <h2 className="text-2xl font-bold text-green-600">
          {t('payment.successTitle', 'Payment Successful')}
        </h2>

        <p className="text-gray-700">{message}</p>

        {purpose === 'bike-rental' && bookingCode && userId && (
          <div className="space-y-4">
            {bookingCodeConfirmed === null && (
              <div className="text-center text-gray-500 text-sm">
                <svg className="animate-spin h-5 w-5 mx-auto mb-2 text-green-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                {t('payment.waitingForConfirmation', 'Waiting for booking confirmation...')}
              </div>
            )}

            {bookingCodeConfirmed === false && (
              <div className="text-red-500 text-sm text-center">
                {t('payment.bookingNotFound', 'Booking could not be verified. Please contact support.')}
              </div>
            )}

            {bookingCodeConfirmed === true && (
              <>
                <div>
                  <p className="text-gray-800 font-semibold">{t('payment.bookingCode', 'Your Booking Code')}:</p>
                  <div className="text-xl font-bold text-green-700">{bookingCode}</div>
                </div>

                <div className="flex justify-center">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeUrl)}`}
                    alt="Booking QR Code"
                    className="w-40 h-40"
                  />
                </div>

                <p className="text-gray-500 text-xs">
                  {t('payment.showQr', 'Show this QR code when asked')} 📲
                </p>

                <a
                  href={qrCodeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-blue-600 underline text-sm mt-2"
                >
                  {t('payment.viewBookingDetails', 'View booking details')}
                </a>
              </>
            )}
          </div>
        )}


        <button
          onClick={handleClose}
          className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-md transition"
        >
          {t('payment.closeButton', 'Close')}
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
